import { InjectionToken } from '@angular/core';
import { LogAction } from '@app/logs/models/enums/log-action.enum';
import { LogType } from '@app/logs/models/enums/log-type.enum';
import { LogTypeOption } from '@app/logs/models/types/log-type-option.type';
import { LogsAPIRequests } from '@app/logs/models/types/logs.types';

export const CURRENTLY_DOWNLOADING_MSG = 'Tried to run database import, but an import file is currently downloading.';

export const DELETED_IMPORT = 'Deleted import';
export const DELETED_EXPORT = 'Deleted export';

export const NO_LOGS_MSG = 'Action succeeded. Log entry exceeds character limit to display.';

export const LOG_TYPE_MAP: Map<LogType, LogTypeOption> = new Map([
    [LogType.ImportExport, {
        value: LogType.ImportExport,
        display: 'Import/export logs',
        route: 'import-export'
    }],
    [LogType.Transformer, {
        value: LogType.Transformer,
        display: 'Transformer logs',
        route: 'transformer'
    }],
    [LogType.FeedAlerts, {
        value: LogType.FeedAlerts,
        display: 'Google Merchant Center logs',
        route: 'feed-alerts'
    }],
    [LogType.Change, {
        value: LogType.Change,
        display: 'Change logs',
        route: 'change'
    }],
    [LogType.ExportEventQueue, {
        value: LogType.ExportEventQueue,
        display: 'Export event queue logs',
        route: 'export-event-queue'
    }],
    [LogType.EDRTS, {
        value: LogType.EDRTS,
        display: 'EDRTS logs',
        route: 'edrts'
    }]
]);

export const FEED_ALERTS_SERVABILITY_MAP: Map<string, number> = new Map([
    ['unaffected', 1],
    ['demoted', 0],
    ['disapproved', -1]
]);

/**
 * Sort FeedAlerts item-level issues: disapproved first, then demoted, then unaffected;
 * within each servability tier, highest item count first.
 */
export function compareFeedAlertsItemLevelIssues(
    a: { servability: string; numItems: string | number },
    b: { servability: string; numItems: string | number }
): number {
    const servability =
        (FEED_ALERTS_SERVABILITY_MAP.get(a.servability) ?? 0) -
        (FEED_ALERTS_SERVABILITY_MAP.get(b.servability) ?? 0);

    if (servability !== 0) {
        return servability;
    }

    return parseInt(String(b.numItems), 10) - parseInt(String(a.numItems), 10);
}

export const FEED_ALERTS_DOWNLOAD_BLACKLIST = new Set([
    '$$hashKey',
    'url',
    'static_api_url',
    'db_id',
    'created_resource_id',
    'api_user_id',
    'request_params',
    'request_body',
    'method',
    'log_method',
    'response_status',
    'response_time',
    'response_body',
    'prior_state'
]);

/** Keep in sync with lib/feedonomics/curl/curl.php CURL_ERROR_TYPED_LOG_MESSAGE_REGEXP */
export const CURL_ERROR_TYPED_LOG_MESSAGE_REGEXP = '^[a-z0-9_]+_curl_error_[0-9]+$';

const CURL_ERROR_TYPED_LOG_MESSAGE_PATTERN = new RegExp(CURL_ERROR_TYPED_LOG_MESSAGE_REGEXP);

/** Keep in sync with lib/feedonomics/service/download/PreprocessErrorLogMessage.php PREPROCESS_ERROR_TYPED_LOG_MESSAGE_REGEXP */
export const PREPROCESS_ERROR_TYPED_LOG_MESSAGE_REGEXP = '^preprocess_error-[a-z0-9_]+$';

const PREPROCESS_ERROR_TYPED_LOG_MESSAGE_PATTERN = new RegExp(PREPROCESS_ERROR_TYPED_LOG_MESSAGE_REGEXP);

/**
 * Important: If a new operation is added to this list of enums,
 * Update the OPERATION_NAMES_MAP constant with a human-readable label
 */
export enum OperationEnum {
    AGGREGATE_DUPLICATE_KEY = 'aggregate_duplicate_key',
    AMAZON_API_ERROR = 'amazon_api_error',
    BAD_HTTP_STATUS_CODE = 'bad_http_status_code',
    BAD_INPUT = 'bad_input',
    CONSECUTIVE_DOWNLOAD_FAILURES_PAUSE = 'consecutive_download_failures_pause',
    CREATE_TABLE_ERROR = 'create_table_error',
    CREATE_TEMPORARY_TABLE_FAIL = 'create_temporary_table_fail',
    CURL_ERROR = 'curl_error',
    DATA_GOVERNANCE_ALERT = 'data_governance_alert',
    DATA_GOVERANCE_PREVENT_EXPORT = 'data_governance_prevent_export',
    DATA_OVERRIDE_UNIQUE_VIOLATION = 'data_override_unique_violation',
    DB_IMPORT = 'db_import',
    DEDUPLICATE_FIELD_ERROR = 'deduplicate_field_error',
    DGQ_PARSE_FAIL = 'dgq_parse_fail',
    DGQ_LEX_FAIL = 'dgq_lex_fail',
    DGQ_CREATE_FAIL = 'dgq_create_fail',
    DGQ_CALL_FAIL = 'dgq_call_fail',
    DOMDOCUMENT_MAX_SIZE = 'domdocument_max_size',
    DOWNLOAD = 'download',
    DOWNLOAD_LIMIT = 'download_limit',
    DOWNLOAD_LIMIT_UNCOMPRESSED = 'download_limit_uncompressed',
    EDSV2_DB_FIELDS_SYNC_FAILED = 'edsv2_db_fields_sync_failed',
    EDSV2_EXPORT_MATERIALIZE_FAILED = 'edsv2_export_materialize_failed',
    EDSV2_EXPORT_NOT_READY = 'edsv2_export_not_ready',
    EDSV2_EXPORT_ROUTED_TO_PHP7_HOST = 'edsv2_export_routed_to_php7_host',
    EDSV2_IMPORT_FAILED = 'edsv2_import_failed',
    EDSV2_QUERY_MATERIALIZE_FAILED = 'edsv2_query_materialize_failed',
    EDSV2_QUERY_ROUTED_TO_PHP7_HOST = 'edsv2_query_routed_to_php7_host',
    EDSV2_ROUTED_TO_PHP7_HOST = 'edsv2_routed_to_php7_host',
    EMPTY_FILE = 'empty_file',
    EXPORT = 'export',
    EXPORT_EMPTY_VIOLATION = 'export_empty_violation',
    EXPORT_EXCLUSION_RULE_ERROR = 'export_exclusion_rule_error',
    EXPORT_JSON_FIELD_INVALID = 'export_json_field_invalid',
    EXPORT_LOCAL = 'export_local',
    EXPORT_MISSING_RAD_ASSOCIATION = 'export_missing_rad_association',
    EXPORT_THRESHOLD_VIOLATION = 'export_threshold_violation',
    EXPORT_WEBHOOK_ERROR = 'export_webhook_error',
    EXPORT_WEBHOOK_LOG = 'export_webhook_log',
    GZIP_FAIL = 'gzip_fail',
    IMPORT_CHUNK_MISMATCH = 'import_chunk_mismatch',
    IMPORT_MISSING_HEADER = 'import_missing_header',
    INVALID_CREDENTIALS = 'invalid_credentials',
    INVALID_URL = 'invalid_url',
    INVALID_XML_PATH = 'invalid_xml_path',
    INVALID_ZIP = 'invalid_zip',
    JSON_TO_CSV = 'json_to_csv',
    LOAD_THRESHOLD_VIOLATION = 'load_threshold_violation',
    LOAD_THRESHOLD_PERCENT_VIOLATION = 'load_threshold_percent_violation',
    MISSING_ITEM_GROUP_ID = 'missing_item_group_id',
    NDJSON_TO_CSV = 'ndjson_to_csv',
    NO_FIELDS_FOR_TEMP_TABLE = 'no_fields_for_tmp_table',
    PREPROCESS_ERROR = 'preprocess_error',
    PUBLISH_AND_CONSUME_RABBITMQ = 'publish_and_consume_rabbitmq',
    SFTP_PERMISSIONS_ERROR = 'sftp_permissions_error',
    SFTP_OPERATIONS_ERROR = 'sftp_operations_error',
    SOURCE_FILE_FIELD_MISSING = 'source_file_field_missing',
    SUMMARY_GOVERNANCE_ALERT = 'summary_governance_alert',
    SUMMARY_GOVERNANCE_CONFIGURATION_ERROR = 'summary_governance_configuration_error',
    SUMMARY_GOVERNANCE_INTERNAL_GM_ERROR = 'summary_governance_internal_gm_error',
    SUMMARY_GOVERNANCE_NO_PRIMARY_KEY = 'summary_governance_no_primary_key',
    SUMMARY_GOVERNANCE_PREVENT_IMPORT = 'summary_governance_prevent_import',
    SUMMARY_GOVERNANCE_PREVENT_EXPORT = 'summary_governance_prevent_export',
    TRANSFORMER_CREATE = 'transformer_create',
    TRANSFORMER_FAIL = 'transformer_fail',
    TRANSFORMER_LEX = 'transformer_lex',
    TRANSFORMER_LEX_STRING = 'transformer_lex_string',
    TRANSFORMER_PARSE = 'transformer_parse',
    TRANSFORMER_PARSE_STRING = 'transformer_parse_string',
    UPDATE_THRESHOLD_VIOLATION = 'update_threshold_violation',
    XML_TO_CSV = 'xml_to_csv',
    XML_TO_CSV_DOWNLOAD = 'xml_to_csv_download'
}

export const OPERATION_NAMES_MAP: Map<string, string> = new Map([
    [OperationEnum.AGGREGATE_DUPLICATE_KEY, 'Aggregate duplicate key'],
    [OperationEnum.AMAZON_API_ERROR, 'Amazon API error'],
    [OperationEnum.BAD_HTTP_STATUS_CODE, 'Bad http status code'],
    [OperationEnum.BAD_INPUT, 'Input file is malformed'],
    [OperationEnum.CONSECUTIVE_DOWNLOAD_FAILURES_PAUSE, 'Auto-paused after consecutive download failures'],
    [OperationEnum.CREATE_TABLE_ERROR, 'Create table error'],
    [OperationEnum.CREATE_TEMPORARY_TABLE_FAIL, 'Create tmp table fail'],
    [OperationEnum.CURL_ERROR, 'Curl error'],
    [OperationEnum.DATA_GOVERNANCE_ALERT, 'Data governance alert'],
    [OperationEnum.DATA_GOVERANCE_PREVENT_EXPORT, 'Data governance prevent export'],
    [OperationEnum.DATA_OVERRIDE_UNIQUE_VIOLATION, 'Data governance unique violation'],
    [OperationEnum.DB_IMPORT, 'Import'],
    [OperationEnum.DEDUPLICATE_FIELD_ERROR, 'Duplicate field error'],
    [OperationEnum.DGQ_PARSE_FAIL, 'DGQ parse fail'],
    [OperationEnum.DGQ_LEX_FAIL, 'DGQ lex fail'],
    [OperationEnum.DGQ_CREATE_FAIL, 'DGQ create fail'],
    [OperationEnum.DGQ_CALL_FAIL, 'DGQ call fail'],
    [OperationEnum.DOMDOCUMENT_MAX_SIZE, 'Document max size'],
    [OperationEnum.DOWNLOAD, 'Download'],
    [OperationEnum.DOWNLOAD_LIMIT, 'Download limit'],
    [OperationEnum.DOWNLOAD_LIMIT_UNCOMPRESSED, 'Uncompressed download limit'],
    [OperationEnum.EDSV2_DB_FIELDS_SYNC_FAILED, 'EDS v2 DB fields sync failed'],
    [OperationEnum.EDSV2_EXPORT_MATERIALIZE_FAILED, 'EDS v2 export materialize failed'],
    [OperationEnum.EDSV2_EXPORT_NOT_READY, 'EDS v2 export not ready'],
    [OperationEnum.EDSV2_EXPORT_ROUTED_TO_PHP7_HOST, 'EDS v2 export routed to PHP 7 host'],
    [OperationEnum.EDSV2_IMPORT_FAILED, 'EDS v2 import failed'],
    [OperationEnum.EDSV2_QUERY_MATERIALIZE_FAILED, 'EDS v2 query materialize failed'],
    [OperationEnum.EDSV2_QUERY_ROUTED_TO_PHP7_HOST, 'EDS v2 query routed to PHP 7 host'],
    [OperationEnum.EDSV2_ROUTED_TO_PHP7_HOST, 'EDS v2 routed to PHP 7 host'],
    [OperationEnum.EMPTY_FILE, 'Empty file'],
    [OperationEnum.EXPORT, 'Export'],
    [OperationEnum.EXPORT_EMPTY_VIOLATION, 'Summary empty violation'],
    [OperationEnum.EXPORT_EXCLUSION_RULE_ERROR, 'Export exclusion rule error'],
    [OperationEnum.EXPORT_JSON_FIELD_INVALID, 'Invalid JSON'],
    [OperationEnum.EXPORT_LOCAL, 'Local export'],
    [OperationEnum.EXPORT_MISSING_RAD_ASSOCIATION, 'RAD export database error'],
    [OperationEnum.EXPORT_THRESHOLD_VIOLATION, 'Export threshold violation'],
    [OperationEnum.EXPORT_WEBHOOK_ERROR, 'Export webhook error'],
    [OperationEnum.EXPORT_WEBHOOK_LOG, 'Export webhook log'],
    [OperationEnum.GZIP_FAIL, 'GZIP fail'],
    [OperationEnum.IMPORT_CHUNK_MISMATCH, 'Import chunk mismatch'],
    [OperationEnum.IMPORT_MISSING_HEADER, 'Import missing header'],
    [OperationEnum.INVALID_CREDENTIALS, 'Invalid credentials'],
    [OperationEnum.INVALID_URL, 'Invalid URL'],
    [OperationEnum.INVALID_XML_PATH, 'Invalid XML path'],
    [OperationEnum.INVALID_ZIP, 'Invalid zip'],
    [OperationEnum.JSON_TO_CSV, 'JSON did not parse'],
    [OperationEnum.LOAD_THRESHOLD_VIOLATION, 'Load threshold violation'],
    [OperationEnum.LOAD_THRESHOLD_PERCENT_VIOLATION, 'Percent last import threshold violation'],
    [OperationEnum.MISSING_ITEM_GROUP_ID, 'Missing item group id'],
    [OperationEnum.NDJSON_TO_CSV, 'NDJSON did not parse'],
    [OperationEnum.NO_FIELDS_FOR_TEMP_TABLE, 'No fields for tmp table'],
    [OperationEnum.PREPROCESS_ERROR, 'Preprocess error'],
    [OperationEnum.PUBLISH_AND_CONSUME_RABBITMQ, 'Publish and consume RabbitMQ'],
    [OperationEnum.SFTP_PERMISSIONS_ERROR, 'SFTP permissions error'],
    [OperationEnum.SFTP_OPERATIONS_ERROR, 'SFTP operations error'],
    [OperationEnum.SOURCE_FILE_FIELD_MISSING, 'Source file field missing'],
    [OperationEnum.SUMMARY_GOVERNANCE_ALERT, 'Summary governance alert'],
    [OperationEnum.SUMMARY_GOVERNANCE_CONFIGURATION_ERROR, 'Summary governance configuration error'],
    [OperationEnum.SUMMARY_GOVERNANCE_PREVENT_EXPORT, 'Summary governance prevent export'],
    [OperationEnum.SUMMARY_GOVERNANCE_PREVENT_IMPORT, 'Summary governance prevent import'],
    [OperationEnum.SUMMARY_GOVERNANCE_INTERNAL_GM_ERROR, 'Summary governance internal error'],
    [OperationEnum.SUMMARY_GOVERNANCE_NO_PRIMARY_KEY, 'Database primary key'],
    [OperationEnum.TRANSFORMER_CREATE, 'Transformer create'],
    [OperationEnum.TRANSFORMER_FAIL, 'Transformer fail'],
    [OperationEnum.TRANSFORMER_LEX, 'Transformer lex'],
    [OperationEnum.TRANSFORMER_LEX_STRING, 'Export selector lex'],
    [OperationEnum.TRANSFORMER_PARSE, 'Transformer parse'],
    [OperationEnum.TRANSFORMER_PARSE_STRING, 'Export selector parse'],
    [OperationEnum.UPDATE_THRESHOLD_VIOLATION, 'Update threshold violation'],
    [OperationEnum.XML_TO_CSV, 'XML to CSV'],
    [OperationEnum.XML_TO_CSV_DOWNLOAD, 'XML to CSV download']
]);

/** Legacy curl_error and typed {service}_curl_error_{errno} log messages. */
export function isCurlErrorLogMessage(message: string): boolean {
    return message === OperationEnum.CURL_ERROR || CURL_ERROR_TYPED_LOG_MESSAGE_PATTERN.test(message);
}

/** Legacy preprocess_error and typed preprocess_error-{subtype} log messages. */
export function isPreprocessErrorLogMessage(message: string): boolean {
    return message === OperationEnum.PREPROCESS_ERROR || PREPROCESS_ERROR_TYPED_LOG_MESSAGE_PATTERN.test(message);
}

export function getOperationDisplayLabel(operation: string): string {
    if (isCurlErrorLogMessage(operation)) {
        return OPERATION_NAMES_MAP.get(OperationEnum.CURL_ERROR) ?? 'Curl error';
    }

    if (isPreprocessErrorLogMessage(operation)) {
        return OPERATION_NAMES_MAP.get(OperationEnum.PREPROCESS_ERROR) ?? 'Preprocess error';
    }

    return OPERATION_NAMES_MAP.get(operation) ?? operation;
}

/**
 * Subset of OPERATION_NAMES_MAP containing only error/failure operations.
*/
export const ERROR_OPERATION_NAMES_MAP: Map<string, string> = new Map([
    [OperationEnum.AGGREGATE_DUPLICATE_KEY, 'Aggregate duplicate key'],
    [OperationEnum.AMAZON_API_ERROR, 'Amazon API error'],
    [OperationEnum.BAD_HTTP_STATUS_CODE, 'Bad http status code'],
    [OperationEnum.BAD_INPUT, 'Input file is malformed'],
    [OperationEnum.CONSECUTIVE_DOWNLOAD_FAILURES_PAUSE, 'Auto-paused after consecutive download failures'],
    [OperationEnum.CREATE_TABLE_ERROR, 'Create table error'],
    [OperationEnum.CREATE_TEMPORARY_TABLE_FAIL, 'Create tmp table fail'],
    [OperationEnum.CURL_ERROR, 'Curl error'],
    [OperationEnum.DATA_GOVERNANCE_ALERT, 'Data governance alert'],
    [OperationEnum.DATA_GOVERANCE_PREVENT_EXPORT, 'Data governance prevent export'],
    [OperationEnum.DATA_OVERRIDE_UNIQUE_VIOLATION, 'Data governance unique violation'],
    [OperationEnum.DEDUPLICATE_FIELD_ERROR, 'Duplicate field error'],
    [OperationEnum.DGQ_CALL_FAIL, 'DGQ call fail'],
    [OperationEnum.DGQ_CREATE_FAIL, 'DGQ create fail'],
    [OperationEnum.DGQ_LEX_FAIL, 'DGQ lex fail'],
    [OperationEnum.DGQ_PARSE_FAIL, 'DGQ parse fail'],
    [OperationEnum.DOMDOCUMENT_MAX_SIZE, 'Document max size'],
    [OperationEnum.DOWNLOAD_LIMIT, 'Download limit'],
    [OperationEnum.DOWNLOAD_LIMIT_UNCOMPRESSED, 'Uncompressed download limit'],
    [OperationEnum.EDSV2_DB_FIELDS_SYNC_FAILED, 'EDS v2 DB fields sync failed'],
    [OperationEnum.EDSV2_EXPORT_MATERIALIZE_FAILED, 'EDS v2 export materialize failed'],
    [OperationEnum.EDSV2_EXPORT_NOT_READY, 'EDS v2 export not ready'],
    [OperationEnum.EDSV2_EXPORT_ROUTED_TO_PHP7_HOST, 'EDS v2 export routed to PHP 7 host'],
    [OperationEnum.EDSV2_IMPORT_FAILED, 'EDS v2 import failed'],
    [OperationEnum.EDSV2_QUERY_MATERIALIZE_FAILED, 'EDS v2 query materialize failed'],
    [OperationEnum.EDSV2_QUERY_ROUTED_TO_PHP7_HOST, 'EDS v2 query routed to PHP 7 host'],
    [OperationEnum.EDSV2_ROUTED_TO_PHP7_HOST, 'EDS v2 routed to PHP 7 host'],
    [OperationEnum.EMPTY_FILE, 'Empty file'],
    [OperationEnum.EXPORT_EMPTY_VIOLATION, 'Summary empty violation'],
    [OperationEnum.EXPORT_EXCLUSION_RULE_ERROR, 'Export exclusion rule error'],
    [OperationEnum.EXPORT_JSON_FIELD_INVALID, 'Invalid JSON'],
    [OperationEnum.EXPORT_MISSING_RAD_ASSOCIATION, 'RAD export database error'],
    [OperationEnum.EXPORT_THRESHOLD_VIOLATION, 'Export threshold violation'],
    [OperationEnum.EXPORT_WEBHOOK_ERROR, 'Export webhook error'],
    [OperationEnum.EXPORT_WEBHOOK_LOG, 'Export webhook log'],
    [OperationEnum.GZIP_FAIL, 'GZIP fail'],
    [OperationEnum.IMPORT_CHUNK_MISMATCH, 'Import chunk mismatch'],
    [OperationEnum.IMPORT_MISSING_HEADER, 'Import missing header'],
    [OperationEnum.INVALID_CREDENTIALS, 'Invalid credentials'],
    [OperationEnum.INVALID_URL, 'Invalid URL'],
    [OperationEnum.INVALID_XML_PATH, 'Invalid XML path'],
    [OperationEnum.JSON_TO_CSV, 'JSON did not parse'],
    [OperationEnum.LOAD_THRESHOLD_VIOLATION, 'Load threshold violation'],
    [OperationEnum.LOAD_THRESHOLD_PERCENT_VIOLATION, 'Percent last import threshold violation'],
    [OperationEnum.MISSING_ITEM_GROUP_ID, 'Missing item group id'],
    [OperationEnum.NDJSON_TO_CSV, 'NDJSON did not parse'],
    [OperationEnum.NO_FIELDS_FOR_TEMP_TABLE, 'No fields for tmp table'],
    [OperationEnum.PREPROCESS_ERROR, 'Preprocess error'],
    [OperationEnum.PUBLISH_AND_CONSUME_RABBITMQ, 'Publish and consume RabbitMQ'],
    [OperationEnum.SFTP_OPERATIONS_ERROR, 'SFTP operations error'],
    [OperationEnum.SFTP_PERMISSIONS_ERROR, 'SFTP permissions error'],
    [OperationEnum.SOURCE_FILE_FIELD_MISSING, 'Source file field missing'],
    [OperationEnum.SUMMARY_GOVERNANCE_ALERT, 'Summary governance alert'],
    [OperationEnum.SUMMARY_GOVERNANCE_CONFIGURATION_ERROR, 'Summary governance configuration error'],
    [OperationEnum.SUMMARY_GOVERNANCE_INTERNAL_GM_ERROR, 'Summary governance internal error'],
    [OperationEnum.SUMMARY_GOVERNANCE_NO_PRIMARY_KEY, 'Database primary key'],
    [OperationEnum.SUMMARY_GOVERNANCE_PREVENT_EXPORT, 'Summary governance prevent export'],
    [OperationEnum.SUMMARY_GOVERNANCE_PREVENT_IMPORT, 'Summary governance prevent import'],
    [OperationEnum.TRANSFORMER_FAIL, 'Transformer fail'],
    [OperationEnum.TRANSFORMER_LEX, 'Transformer lex'],
    [OperationEnum.TRANSFORMER_LEX_STRING, 'Export selector lex'],
    [OperationEnum.TRANSFORMER_PARSE_STRING, 'Export selector parse'],
    [OperationEnum.UPDATE_THRESHOLD_VIOLATION, 'Update threshold violation']
]);

export const IMPORT_EXPORT_LOGS_OPERATIONS_MAP_TOKEN = new InjectionToken<Map<string, string>>(
    'import_export_logs_operations_map',
    { providedIn: 'root', factory: (): Map<string, string> => OPERATION_NAMES_MAP }
);

export const IMPORT_EXPORT_LOGS_DEFAULT_OPERATIONS_TOKEN = new InjectionToken<Map<string, string>>(
    'import_export_logs_default_operations',
    { providedIn: 'root', factory: (): Map<string, string> => new Map() }
);

export const EXCLUSION_RULE_ROUTES: Set<string> = new Set([
    'POST|/dbs/:db_id/exports/:export_id/export_exclusion_rules',
    'PUT|/dbs/:db_id/exports/:export_id/export_exclusion_rules/:rule_id',
    'DELETE|/dbs/:db_id/exports/:export_id/export_exclusion_rules/:rule_id'
]);

export const LOGS_API_REQUESTS_MAP: LogsAPIRequests = new Map([
    ['GET|/login', {
        category: 'login',
        action: LogAction.Execute,
        display: 'Login'
    }],
    ['GET|/login/:username/:password', { // Legacy
        category: 'login',
        action: LogAction.Execute,
        display: 'Login'
    }],
    ['PUT|/accept_invite', {
        category: 'invitations',
        action: LogAction.Update,
        display: 'Accept invitation'
    }],
    ['PUT|/forgot_password', {
        category: 'password',
        action: LogAction.Update,
        display: 'Forgot password request'
    }],
    ['PUT|/reset_password', {
        category: 'password',
        action: LogAction.Update,
        display: 'Password'
    }],
    ['POST|/accounts/:account_id/user_account_permissions', {
        category: 'permissions',
        action: LogAction.Create,
        display: 'Account permissions'
    }],
    ['DELETE|/accounts/:account_id/user_account_permissions/:uap_id', {
        category: 'permissions',
        action: LogAction.Delete,
        display: 'Account permissions'
    }],
    ['POST|/dbs/:db_id/user_db_permissions', {
        category: 'permissions',
        action: LogAction.Create,
        display: 'Database permissions'
    }],
    ['DELETE|/dbs/:db_id/user_db_permissions/:udp_id', {
        category: 'permissions',
        action: LogAction.Delete,
        display: 'Database permissions'
    }],
    ['PUT|/dbs/:db_id/user_db_permissions', {
        category: 'permissions',
        action: LogAction.Update,
        display: 'Database permissions'
    }],
    ['PUT|/user/update_account_permission/:uap_id', {
        category: 'permissions',
        action: LogAction.Update,
        display: 'Account permissions'
    }],
    ['PUT|/user/update_db_permission/:udp_id', {
        category: 'permissions',
        action: LogAction.Update,
        display: 'Database permissions'
    }],
    ['POST|/stripe', {
        category: 'stripe',
        action: LogAction.Create,
        display: 'Stripe authorization'
    }],
    ['POST|/dbs/:db_id/transformed_data_overrides/:export_id', {
        category: 'transformed_data_overrides',
        action: LogAction.CreateUpdate,
        display: 'Transformed data overrides',
        bcDisplay: 'Overrides'
    }],
    ['POST|/dbs/:db_id/transformed_data_overrides', {
        category: 'transformed_data_overrides',
        action: LogAction.Delete,
        display: 'Transformed data overrides',
        bcDisplay: 'Overrides'
    }],
    ['POST|/dbs/:db_id/exports/:export_id/export_exclusion_rules', {
        category: 'export_exclusion_rules',
        action: LogAction.Create,
        display: 'Exclusion Rule'
    }],
    ['PUT|/dbs/:db_id/exports/:export_id/export_exclusion_rules/:rule_id', {
        category: 'export_exclusion_rules',
        action: LogAction.Update,
        display: 'Exclusion Rule'
    }],
    ['DELETE|/dbs/:db_id/exports/:export_id/export_exclusion_rules/:rule_id', {
        category: 'export_exclusion_rules',
        action: LogAction.Delete,
        display: 'Exclusion Rule'
    }],
    ['POST|/dbs/:db_id/custom_label_rules/create_rule', {
        category: 'custom_label_rules',
        action: LogAction.Create,
        display: 'Custom Label Rule'
    }],
    ['POST|/dbs/:db_id/exports/:export_id/custom_label_rules', {
        category: 'custom_label_rules',
        action: LogAction.Create,
        display: 'Custom Label Rule'
    }],
    ['PUT|/dbs/:db_id/exports/:export_id/custom_label_rules/:rule_id', {
        category: 'custom_label_rules',
        action: LogAction.Update,
        display: 'Custom Label Rule'
    }],
    ['DELETE|/dbs/:db_id/exports/:export_id/custom_label_rules/:rule_id', {
        category: 'custom_label_rules',
        action: LogAction.Delete,
        display: 'Custom Label Rule'
    }],
    ['POST|/accounts/:account_id/dbs', {
        category: 'dbs',
        action: LogAction.Create,
        display: 'Database'
    }],
    ['POST|/dbs/:db_id/copy_db', {
        category: 'dbs',
        action: LogAction.Copy,
        display: 'Database'
    }],
    ['DELETE|/dbs/:db_id', {
        category: 'dbs',
        action: LogAction.Delete,
        display: 'Database'
    }],
    ['PUT|/dbs/:db_id', {
        category: 'dbs',
        action: LogAction.Update,
        display: 'Database'
    }],
    ['PUT|/dbs/:db_id/pause', {
        category: 'pause_dbs',
        action: LogAction.Update,
        display: 'Database'
    }],
    ['PUT|/dbs/:db_id/unpause', {
        category: 'pause_dbs',
        action: LogAction.Update,
        display: 'Database'
    }],
    ['POST|/dbs/:db_id/primary_key', {
        category: 'dbs',
        action: LogAction.Update,
        display: 'DB primary key'
    }],
    ['PUT|/dbs/:db_id/rename', {
        category: 'dbs',
        action: LogAction.Update,
        display: 'Database'
    }],
    ['PUT|/dbs/:db_id/email_address', {
        category: 'dbs',
        action: LogAction.Update,
        display: 'Email address (alerts)'
    }],
    ['PUT|/dbs/:db_id/report_account', {
        category: 'third_party_accounts',
        action: LogAction.Update,
        display: 'Google account'
    }],
    ['PUT|/dbs/:db_id/report_account_google_analytics', {
        category: 'third_party_accounts',
        action: LogAction.Update,
        display: 'Google anaylitics account'
    }],
    ['PUT|/dbs/:db_id/report_account_bing', {
        category: 'third_party_accounts',
        action: LogAction.Update,
        display: 'Bing account/campaign'
    }],
    ['POST|/dbs/:db_id/imports', {
        category: 'imports',
        action: LogAction.Create,
        display: 'Import',
        bcDisplay: 'Data source'
    }],
    ['POST|/dbs/:db_id/imports/:import_id', {
        category: 'imports',
        action: LogAction.Update,
        display: 'Import',
        bcDisplay: 'Data source'
    }],
    ['PUT|/dbs/:db_id/imports/:import_id/update_import_fields', {
        category: 'imports',
        action: LogAction.Update,
        display: 'Import field update',
        bcDisplay: 'Data source field update'
    }],
    ['PUT|/dbs/:db_id/imports/:import_id/schedule', {
        category: 'imports',
        action: LogAction.Update,
        display: 'Import schedule',
        bcDisplay: 'Data source sync frequency'
    }],
    ['DELETE|/dbs/:db_id/imports/:import_id', {
        category: 'imports',
        action: LogAction.Delete,
        display: 'Import',
        bcDisplay: 'Data source'
    }],
    ['PUT|/dbs/:db_id/imports/:import_id/delimiters', {
        category: 'imports',
        action: LogAction.Update,
        display: 'Import delimiters',
        bcDisplay: 'Data source delimiters'
    }],
    ['PUT|/dbs/:db_id/imports/:import_id/xml_force_parse', {
        category: 'imports',
        action: LogAction.Update,
        display: 'Import XML force parse',
        bcDisplay: 'Data source XML force parse'
    }],
    ['PUT|/dbs/:db_id/imports/:import_id/file_map', {
        category: 'imports',
        action: LogAction.Update,
        display: 'Import file map',
        bcDisplay: 'Data source file map'
    }],
    ['PUT|/dbs/:db_id/imports/:import_id/thresholds', {
        category: 'imports',
        action: LogAction.Update,
        display: 'Import thresholds',
        bcDisplay: 'Data source thresholds'
    }],
    ['PUT|/dbs/:db_id/imports/:import_id/retries', {
        category: 'imports',
        action: LogAction.Update,
        display: 'Import retries',
        bcDisplay: 'Data source retries'
    }],
    ['PUT|/dbs/:db_id/extra_import_fields', {
        category: 'imports',
        action: LogAction.Update,
        display: 'Extra import fields',
        bcDisplay: 'Extra data source fields'
    }],
    ['POST|/dbs/:db_id/transformers', {
        category: 'transformers',
        action: LogAction.Create,
        display: 'Transformer',
        bcDisplay: 'Rule'
    }],
    ['DELETE|/dbs/:db_id/transformers', {
        category: 'transformers',
        action: LogAction.Delete,
        display: 'Transformer',
        bcDisplay: 'Rule'
    }],
    ['DELETE|/dbs/:db_id/transformers/:transformer_id', {
        category: 'transformers',
        action: LogAction.Delete,
        display: 'Transformer',
        bcDisplay: 'Rule'
    }],
    ['DELETE|/dbs/:db_id/transformers/:field_name', {
        category: 'transformers',
        action: LogAction.Delete,
        display: 'Transformer',
        bcDisplay: 'Rule'
    }],
    ['PUT|/dbs/:db_id/transformers/:transformer_id', {
        category: 'transformers',
        action: LogAction.Update,
        display: 'Transformer',
        bcDisplay: 'Rule'
    }],
    ['PUT|/dbs/:db_id/transformers/:transformer_id/sort_order', {
        category: 'transformers',
        action: LogAction.Update,
        display: 'Transformer order',
        bcDisplay: 'Rule order'
    }],
    ['POST|/dbs/:db_id/transformers/tokenize-old', {
        category: 'transformers',
        action: LogAction.Create,
        display: 'Tokenize transformers',
        bcDisplay: 'Tokenize rules'
    }],
    ['PUT|/dbs/:db_id/db_fields/', {
        category: 'db_fields',
        action: LogAction.Update,
        display: 'DB fields update'
    }],
    ['POST|/dbs/:db_id/exports', {
        category: 'exports',
        action: LogAction.Create,
        display: 'Export',
        bcDisplay: 'Channel'
    }],
    ['PUT|/dbs/:db_id/exports/:export_id', {
        category: 'exports',
        action: LogAction.Update,
        display: 'Export',
        bcDisplay: 'Channel'
    }],
    ['PUT|/dbs/:db_id/exports/:export_id/schedule', {
        category: 'exports',
        action: LogAction.Update,
        display: 'Export schedule',
        bcDisplay: 'Channel sync frequency'
    }],
    ['DELETE|/dbs/:db_id/exports/:export_id', {
        category: 'exports',
        action: LogAction.Delete,
        display: 'Export',
        bcDisplay: 'Channel'
    }],
    ['POST|/dbs/:db_id/exports/:export_id/run_parallel_export', {
        category: 'exports',
        action: LogAction.Execute,
        display: 'Push export',
        bcDisplay: 'Channel sync'
    }],
    ['POST|/dbs/:db_id/exports/:export_id/run_export', {
        category: 'exports',
        action: LogAction.Execute,
        display: 'Push export',
        bcDisplay: 'Channel sync'
    }],
    ['POST|/dbs/:db_id/run_scheduled_exports', {
        category: 'exports',
        action: LogAction.Execute,
        display: 'Push exports',
        bcDisplay: 'Channel sync'
    }],
    ['POST|/dbs/:db_id/run_scheduled_imports', {
        category: 'imports',
        action: LogAction.Execute,
        display: 'Run imports',
        bcDisplay: 'Data source sync'
    }],
    ['POST|/dbs/:db_id/exports/:export_id/data_governance_queries', {
        category: 'exports',
        action: LogAction.Create,
        display: 'Data governance'
    }],
    ['POST|/dbs/:db_id/exports/:export_id/download_sample', {
        category: 'exports',
        action: LogAction.Execute,
        display: 'Feed AI download sample'
    }],
    ['PUT|/dbs/:db_id/exports/:export_id/data_governance_queries/:data_governance_query_id', {
        category: 'exports',
        action: LogAction.Update,
        display: 'Data governance'
    }],
    ['DELETE|/dbs/:db_id/exports/:export_id/data_governance_queries/:data_governance_query_id', {
        category: 'exports',
        action: LogAction.Delete,
        display: 'Data governance'
    }],
    ['POST|/dbs/:db_id/unique_values', {
        category: 'unique_values',
        action: LogAction.Create,
        display: 'Set map for a field'
    }],
    ['POST|/dbs/:db_id/join_performance_report', {
        category: 'join_performance_report',
        action: LogAction.Execute,
        display: 'Join performance report'
    }],
    ['POST|/dbs/:db_id/ftp_triggers', {
        category: 'ftp',
        action: LogAction.Create,
        display: 'FTP triggers'
    }],
    ['DELETE|/dbs/:db_id/ftp_triggers/:trigger_id', {
        category: 'ftp',
        action: LogAction.Delete,
        display: 'FTP triggers'
    }],
    ['POST|/dbs/:db_id/amazon_authorization', {
        category: 'authorization',
        action: LogAction.Create,
        display: 'Amazon authorization'
    }],
    ['POST|/dbs/:db_id/sears_authorization', {
        category: 'authorization',
        action: LogAction.Create,
        display: 'Sears authorization'
    }],
    ['POST|/dbs/:db_id/walmart_authorization', {
        category: 'authorization',
        action: LogAction.Create,
        display: 'Walmart authorization'
    }],
    ['POST|/dbs/:db_id/mercado_libre_authorization', {
        category: 'authorization',
        action: LogAction.Create,
        display: 'Mercado libre authorization'
    }],
    ['POST|/dbs/:db_id/ftp_authorization', {
        category: 'authorization',
        action: LogAction.Create,
        display: 'FTP authorization'
    }],
    ['POST|/dbs/:db_id/polyvore_authorization', {
        category: 'authorization',
        action: LogAction.Create,
        display: 'Polyvore authorization'
    }],
    ['POST|/dbs/:db_id/google_merchant_center_authorization', {
        category: 'authorization',
        action: LogAction.Create,
        display: 'Google Merchant Center authorization'
    }],
    ['POST|/dbs/:db_id/gmc_threshold', {
        category: 'feed_alerts',
        action: LogAction.Create,
        display: 'Google Merchant Center alert creation'
    }],
    ['POST|/dbs/:db_id/gmc_shopping_actions/test_order', {
        category: 'google_merchant_center',
        action: LogAction.Execute,
        display: 'Google Merchant Center test order'
    }],
    ['POST|/dbs/:db_id/feed_config', {
        category: 'feed_config',
        action: LogAction.Update,
        display: 'Feed pull configuration'
    }],
    ['POST|/dbs/:db_id/ftp_accounts', {
        category: 'ftp',
        action: LogAction.Create,
        display: 'FTP account'
    }],
    ['PUT|/dbs/:db_id/ftp_accounts', {
        category: 'ftp',
        action: LogAction.Update,
        display: 'FTP account'
    }],
    ['PUT|/dbs/:db_id/external_id', {
        category: 'dbs',
        action: LogAction.Update,
        display: 'DB external ID'
    }],
    ['PUT|/dbs/:db_id/transformer_notes', {
        category: 'transformers',
        action: LogAction.Update,
        display: 'Transformer notes',
        bcDisplay: 'Rule notes'
    }],
    ['POST|/dbs/:db_id/bulk_transformers', {
        category: 'transformers',
        action: LogAction.Create,
        display: 'Bulk transformers',
        bcDisplay: 'Bulk rules'
    }],
    ['POST|/emails/send_auth_email', {
        category: 'emails',
        action: LogAction.Create,
        display: 'Send email'
    }],
    ['POST|/dbs/:db_id/run_imports', {
        category: 'imports',
        action: LogAction.Execute,
        display: 'Run imports',
        bcDisplay: 'Data source sync'
    }],
    ['POST|/dbs/:db_id/google_service_account', {
        category: 'authorization',
        action: LogAction.Create,
        display: 'Google Service Account'
    }],
    ['PUT|/dbs/:db_id/google_service_account', {
        category: 'authorization',
        action: LogAction.Copy,
        display: 'Google Service Account'
    }],
    ['POST|/dbs/:db_id/classifier_info', {
        category: 'exports',
        action: LogAction.Update,
        display: 'Feed AI classifier'
    }],
    ['POST|/dbs/:db_id/clear_cache', {
        category: 'exports',
        action: LogAction.Delete,
        display: 'Feed AI clear cache'
    }],
    ['POST|/dbs/:db_id/feed_summary_governance', {
        category: 'summary_governance',
        action: LogAction.Create,
        display: 'Summary governance'
    }],
    ['PUT|/dbs/:db_id/feed_summary_governance/:summary_id', {
        category: 'summary_governance',
        action: LogAction.Update,
        display: 'Summary governance'
    }],
    ['POST|/dbs/:db_id/feed_summary_governance/:summary_id/threshold', {
        category: 'summary_governance',
        action: LogAction.Create,
        display: 'Summary governance threshold'
    }],
    ['PUT|/dbs/:db_id/feed_summary_governance/:summary_id/threshold/:threshold_id', {
        category: 'summary_governance',
        action: LogAction.Update,
        display: 'Summary governance threshold'
    }],
    ['DELETE|/dbs/:db_id/feed_summary_governance/:summary_id/threshold/:threshold_id', {
        category: 'summary_governance',
        action: LogAction.Delete,
        display: 'Summary governance threshold'
    }]
]);

/**
 * This is only used for old log entries
 */
export const HUMAN_FRIENDLY_ERRORS_MAP: Map<string, string> = new Map([
    ['general_spam_policy', 'Automatic item disapproval due to policy violation'],
    ['generic_landing_page_redirect', 'Redirect to generic landing page'],
    ['image_link_broken', 'Invalid images'],
    ['image_link_generic', 'Generic images'],
    ['image_link_missing', 'Missing images'],
    ['image_link_roboted', 'Images cannot be crawled because of robots.txt restriction'],
    ['image_link_slow_crawl', 'Ensure that Google can access and crawl your images'],
    ['image_link_too_small', 'Images too small'],
    ['landing_page_error', 'Product pages cannot be accessed'],
    ['landing_page_roboted', 'Product pages cannot be crawled because of robots.txt restriction'],
    ['missing_required_gtin_error', 'Item requires a GTIN'],
    ['mobile_landing_page_error', 'Product pages cannot be accessed from a mobile device'],
    ['mobile_landing_page_roboted', 'Product pages cannot be crawled with a mobile user-agent because of robots.txt restriction'],
    ['forbidden_bulk_gtin_error', 'Item uses a GTIN reserved for bulk products when a valid GTIN is required'],
    ['image_link_small_warning', 'Image too small'],
    ['missing_required_gtin_warning', 'Item requires a GTIN'],
    ['voila_hammer_dangerous_knives', 'Items preemptively disapproved for a policy violation (Dangerous products: dangerous knives)'],
    ['voila_hammer_other_weapons', 'Items preemptively disapproved for a policy violation (Dangerous products: other weapons)'],
    ['wrong_tax', 'Incorrect tax information'],
    ['forbidden_coupon_gtin_error', 'Item uses a coupon GTIN when a valid GTIN is required'],
    ['forbidden_coupon_gtin_warning', 'Coupon code GTIN warning'],
    ['invalid_mime_type', 'Item landing page does not render properly'],
    ['missing_required_gtin_future_', 'Missing Required GTIN'],
    ['mismatching_brand_gtin_prefix_', 'Mismatching brand, gtin_'],
    ['price_mismatch', 'Temporary item disapprovals due to incorrect prices'],
    ['strong_id_inaccurate', 'Incorrect product identifiers'],
    ['title_all_caps', 'Capitalized titles'],
    ['image_link_missing_local_offer', 'Local items - missing image'],
    ['local_missing_description', 'Missing description'],
    ['local_offer_unmatchable', 'Local offer not matching any catalog or online offer'],
    ['availability_mismatch', 'Temporary item disapprovals due to incorrect availability'],
    ['missing_required_item_color', 'Missing Color'],
    ['missing_required_item_size', 'Missing Size'],
    ['multiple_sizes', 'Multiple sizes for one item'],
    ['wrong_shipping', 'Incorrect shipping costs'],
    ['availability_updated', 'Incorrect microdata availability information'],
    ['dq_pid_no_fresh_crawl', 'Preemptive disapproval for price'],
    ['dq_pid_price_no_extraction', 'Preemptive disapproval for price'],
    ['non_normalizable_sizes', 'Size not recognised'],
    ['title_long', 'Long titles']
]);

// Infinite scrolling constants
export const LOGS_MAX_RETRY_COUNT: number = 5;
export const LOGS_MAX_CACHE_SIZE: number = 5;
export const LOGS_MIN_ADDED_ROWS: number = 20;
export const LOGS_DEFAULT_PAGE_SIZE: number = 100;
